import { CustomerApplicationFilterOptions, CustomerApplicationTagEntityQueryOptions, CustomerApplicationTemplateEntityQueryOptions, CustomerApplicationTemplateTagDto } from "Hooks/Api/apiCustomerApplications";
import { requestUsersApi } from "Hooks/Api/apiUsers";
import { useCustomerApplicationTemplates, useCustomerApplicationTemplateTags } from "Hooks/ApiHooks/useCustomerApplicationTemplates";
import { ApplicationEntity, UserEntity } from "Models/Entities";
import { store } from "Models/Store";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCustomerApplicationPageParams } from "./useCustomerApplicationPageParams";

const DefaultFilters: CustomerApplicationFilterOptions = {
	filterByCustomerApplicationTagIds: [],
	filterByStatus: ['ALL'],
	filterByCreatedDateRange: [],
	filterBySubmittedDateRange: [],
	filterByDecisionDateRange: [],
	filterByCustomerApplicationFormIds: [],
	filterByUserIds: [],
}

function mapUserEntityListToOptions(data: UserEntity[]) {
	return data.map(user => ({
		display: `${user.firstName} ${user.lastName}`,
		value: user.id,
	}));
}

function mapCustomerApplicationTemplateListToOptions(data: ApplicationEntity[]) {
	return data.map(application => ({
		display: application.applicationName,
		value: application.id,
	}));
}

function mapCustomerApplicationTemplateTagListToOptions(data: CustomerApplicationTemplateTagDto[]) {
	return data
		.sort((a, b) => a.name.localeCompare(b.name))
		.map(x => ({
			display: x.name,
			value: x.id,
		}));
}

function getInitialState(props?: CustomerApplicationFilterOptions) {
	const initFilters = {
		...DefaultFilters,
		...props
	}

	const result = {
		filters: initFilters,
		showFilter: props?.filterByStatus != null || props?.filterByUserIds != null,
	}
	return result;
}

/**
 * Customer Application Page has bunch of filters all dependent on request. 
 * 
 * Hook for managing filtering logic on the Customer Application Page.
 * - managing filter states
 * - fetching filter options
 */
export function useCustomerApplicationPageFilters() {

	// Grab any prefilled params from the url
	const pageParams = useCustomerApplicationPageParams();
	const initState = getInitialState(pageParams);

	const [showFilters, setShowFilters] = useState(initState.showFilter);
	const [filters, setFilters] = useState<CustomerApplicationFilterOptions>(initState.filters);
	const isFirstRender = useRef(true);

	function resetFilters() {
		setShowFilters(false);
		setFilters(DefaultFilters);
	}

	useEffect(() => {
		// Skip on initial render so we don't clear the filters from the page params
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		resetFilters(); // Runs only on subsequent changes
	}, [store.impersonatingOrganisationId]);

	const getUserOptions = async (searchTerm?: string) => {
		if (!store.impersonatingOrganisationId && !store.getUser?.organisation?.id) {
			return [];
		}
		const response = await requestUsersApi({
			searchBy: searchTerm,
			searchOn: ['firstName', 'lastName'],
			filterByActiveUser: true,
			filterByOrgIds: store.impersonatingOrganisationId ? [store.impersonatingOrganisationId] : [store.getUser!.organisation!.id],
		});

		return mapUserEntityListToOptions(response.data.data);
	}

	const reqCustomerApplicationTemplatesFilters: CustomerApplicationTemplateEntityQueryOptions = useMemo(() => {
		return ({
			filterByBusinessIds: store.getUser?.organisation?.businessEntitys.map(x => x.id),
			filterByUserIds: store.getUser?.id ? [store.getUser.id] : [],
		})
	}, [store.getUser?.organisation?.businessEntitys, store.getUser?.id, store.impersonatingOrganisationId]);

	const reqCustomerApplicationTemplates = useCustomerApplicationTemplates(reqCustomerApplicationTemplatesFilters);

	const getCustomerApplicationFormOptions = async (searchTerm?: string): Promise<{ display: string, value: string }[]> => {
		return mapCustomerApplicationTemplateListToOptions(reqCustomerApplicationTemplates?.data?.data ?? [])
	};

	const reqCustomerApplicationTemplateTagFilters: CustomerApplicationTagEntityQueryOptions = useMemo(() => {
		return ({
			filterByCustomerApplicationFormIds: (reqCustomerApplicationTemplates.data?.data ?? []).map(x => x.id)
		})
	}, [reqCustomerApplicationTemplates.data?.data]);

	const reqCustomerApplicationTemplateTags = useCustomerApplicationTemplateTags(reqCustomerApplicationTemplateTagFilters);

	const getCustomerApplicationTemplateTagOptions = async (searchTerm?: string): Promise<{ display: string, value: string }[]> => {
		return mapCustomerApplicationTemplateTagListToOptions(reqCustomerApplicationTemplateTags?.data?.data ?? [])
	};

	return {
		// Filter states
		filters,
		setFilters,
		resetFilters,
		showFilters,
		setShowFilters,

		// Functions for fetching options for the filter inputs
		getUserOptions,
		getCustomerApplicationFormOptions,
		getCustomerApplicationTemplateTagOptions,

		// Errors used for displaying on filter inputs
		fetchingOptionsErrors: {
			customerApplicationTemplates: reqCustomerApplicationTemplates?.error?.message,
			customerApplicationTemplateTags: reqCustomerApplicationTemplateTags?.error?.message
		}
	}
}